import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { sessionService } from '../../../services/sessionService';
import { userService } from '../../../services/userService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import ExportExcelButton from '../../common/ExportExcelButton';
import DebouncedInput from '../../ui/DebouncedInput';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { USER_ROLES } from '../../../constants/authConstants';
import { useFilters } from '../../../hooks/useFilters';
import { formatSessionAmount, DEFAULT_SESSION_PROJECT, SESSION_CLEARANCE_TYPE_OPTIONS, SESSION_PROJECT_STATUS_OPTIONS } from '../../../constants/sessionConstants';
import { financialYearUtils, debounce, dateUtils } from '../../../utils/helpers';
import { validateSessionProject } from '../../../utils/sessionValidation';
import { exportSessionBudgetDescriptiveToExcel } from '../../../utils/excelExport';
import { LOCK_MESSAGES } from '../../../constants/projectLockConstants';

const SessionBudgetDescriptive = () => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel, getDepartmentOptions } = useDepartments();
  
  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYearUtils.getActualCurrentFinancialYear()
  );
  
  const [viewAllYears, setViewAllYears] = useState(false);
  
  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: financialYearUtils.getActualCurrentFinancialYear(),
    department: isAdmin ? '' : user?.department || '',
    clearanceType: '',
    projectStatus: ''
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState({});
  const [errors, setErrors] = useState({});
  const [userCache, setUserCache] = useState({});
  const [showUpdatedByColumn, setShowUpdatedByColumn] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  
  const [totals, setTotals] = useState({
    totalFinancialRequirement: 0,
    totalStatePlan: 0,
    totalNegotiatedLoan: 0,
    totalProjects: 0,
    totalNewProjects: 0,
    totalOngoingProjects: 0,
    newProjectsAmount: 0,
    ongoingProjectsAmount: 0
  });

  const [departmentTotals, setDepartmentTotals] = useState({});
  
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const filterConfig = getFilterConfig('SESSION_DESCRIPTIVE', {
    showDepartmentFilter: isAdmin
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const safeParseFloat = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    if (typeof value === 'string') {
      const cleanedValue = value.replace(/[^\d.-]/g, '');
      const numValue = parseFloat(cleanedValue);
      return isNaN(numValue) ? 0 : numValue;
    }
    const numValue = Number(value);
    return isNaN(numValue) ? 0 : numValue;
  };

  // USER MANAGEMENT FUNCTIONS
  const getUserDisplayName = async (userId) => {
    if (!userId) return 'System';
    if (userCache[userId]) return userCache[userId];
    
    try {
      const userData = await userService.getUserById(userId);
      const displayName = userData ? 
        userData.fullName || userData.email || 'Unknown User'
        : 'Unknown User';
      
      setUserCache(prev => ({ ...prev, [userId]: displayName }));
      return displayName;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return 'Unknown User';
    }
  };

  const formatLastUpdated = (updatedBy, updatedAt) => {
    const userDisplayName = userCache[updatedBy] || 'Loading...';
    const timeAgo = updatedAt ? dateUtils.formatDateTime(updatedAt) : 'Unknown';
    return { user: userDisplayName, time: timeAgo };
  };

  const loadUserData = async (projectsList) => {
    const userIds = [...new Set(projectsList.map(p => p.updatedBy || p.createdBy).filter(Boolean))];
    for (const userId of userIds) {
      if (!userCache[userId]) await getUserDisplayName(userId);
    }
  };

  const calculateDepartmentTotals = (projectsList) => {
    const totalsObj = {};
    
    projectsList.forEach((project) => {
      const dept = project.department;
      if (!dept) return;
      
      if (!totalsObj[dept]) totalsObj[dept] = 0;
      
      const numericAmount = safeParseFloat(project.amount);
      const currentTotal = Number(totalsObj[dept]) || 0;
      totalsObj[dept] = currentTotal + numericAmount;
    });
    
    return totalsObj;
  };

  // DATA LOADING
  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const fyToLoad = viewAllYears ? null : selectedFinancialYear;
      
      const result = await sessionService.getSessionDescriptiveData(
        fyToLoad,
        filters.department
      );
      
      if (result.success) {
        const cleanedProjects = (result.data.projects || []).map(project => ({
          ...project,
          amount: safeParseFloat(project.amount),
          isProjectLocked: project.isProjectLocked || false,
          projectLockedAt: project.projectLockedAt || null,
          projectLockedBy: project.projectLockedBy || null,
          projectLockedByName: project.projectLockedByName || null
        }));
        
        setProjects(cleanedProjects);
        setTotals(result.data.totals || {
          totalFinancialRequirement: 0,
          totalStatePlan: 0,
          totalNegotiatedLoan: 0,
          totalProjects: 0,
          totalNewProjects: 0,
          totalOngoingProjects: 0,
          newProjectsAmount: 0,
          ongoingProjectsAmount: 0
        });
        
        await loadUserData(cleanedProjects);
      }
    } catch (error) {
      console.error('Error loading session descriptive data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!viewAllYears) {
      handleFilterChange('financialYear', selectedFinancialYear);
    }
  }, [selectedFinancialYear, viewAllYears]);

  useEffect(() => {
    if (departments.length > 0) {
      loadSessionData();
    }
  }, [selectedFinancialYear, viewAllYears, filters.department, departments]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [projects, filters.search, filters.clearanceType, filters.projectStatus, departments]);

  // FILTERING
  const applyFiltersAndSort = () => {
    let filtered = [...projects];
    
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(project => {
        return project.projectName?.toLowerCase().includes(searchLower) ||
               project.source?.toLowerCase().includes(searchLower) ||
               getDepartmentLabel(project.department).toLowerCase().includes(searchLower);
      });
    }
    
    if (filters.clearanceType) {
      filtered = filtered.filter(project => project.clearanceType === filters.clearanceType);
    }
    
    if (filters.projectStatus) {
      filtered = filtered.filter(project => project.projectStatus === filters.projectStatus);
    }
    
    const filteredTotals = {
      totalFinancialRequirement: filtered.reduce((sum, p) => sum + safeParseFloat(p.amount), 0),
      totalStatePlan: filtered.filter(p => p.clearanceType === 'state_plan').reduce((sum, p) => sum + safeParseFloat(p.amount), 0),
      totalNegotiatedLoan: filtered.filter(p => p.clearanceType === 'negotiated_loan').reduce((sum, p) => sum + safeParseFloat(p.amount), 0),
      totalProjects: filtered.length,
      totalNewProjects: filtered.filter(p => p.projectStatus === 'new_project').length,
      totalOngoingProjects: filtered.filter(p => p.projectStatus === 'ongoing_project').length,
      newProjectsAmount: filtered.filter(p => p.projectStatus === 'new_project').reduce((sum, p) => sum + safeParseFloat(p.amount), 0),
      ongoingProjectsAmount: filtered.filter(p => p.projectStatus === 'ongoing_project').reduce((sum, p) => sum + safeParseFloat(p.amount), 0)
    };

    const deptTotals = calculateDepartmentTotals(filtered);
    
    setFilteredProjects(filtered);
    setTotals(filteredTotals);
    setDepartmentTotals(deptTotals);
  };

  // PROJECT-LEVEL LOCK/UNLOCK FUNCTIONS
  const handleLockProject = async (projectId) => {
    if (!projectId) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (project.isProjectLocked) {
      alert(LOCK_MESSAGES.PROJECT_ALREADY_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.PROJECT_LOCK_CONFIRM)) return;

    try {
      const result = await sessionService.lockSessionProject(
        projectId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, isProjectLocked: true, projectLockedAt: new Date(), projectLockedBy: user.uid, projectLockedByName: user.displayName || user.email }
            : p
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.PROJECT_LOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error locking project:', error);
      alert('Failed to lock project: ' + error.message);
    }
  };

  const handleUnlockProject = async (projectId) => {
    if (!isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCK_NO_PERMISSION);
      return;
    }

    if (!projectId) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (!project.isProjectLocked) {
      alert(LOCK_MESSAGES.PROJECT_NOT_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.PROJECT_UNLOCK_CONFIRM)) return;

    try {
      const result = await sessionService.unlockSessionProject(
        projectId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, isProjectLocked: false, projectLockedAt: null, projectLockedBy: null, projectLockedByName: null }
            : p
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.PROJECT_UNLOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error unlocking project:', error);
      alert('Failed to unlock project: ' + error.message);
    }
  };

  const canEditProject = (project) => {
    if (isAdmin) return true;
    return !project.isProjectLocked;
  };

  // AUTO-SAVE WITH DEBOUNCE
  const debouncedAutoSave = useCallback(
    debounce(async (projectId, projectData) => {
      const project = projects.find(p => p.id === projectId);
      
      if (!project) return;

      if (project.isProjectLocked && !isAdmin) {
        console.log('Cannot save: project is locked');
        return;
      }

      try {
        setSaving(prev => ({ ...prev, [projectId]: true }));
        
        const dataToSave = {
          ...projectData,
          amount: safeParseFloat(projectData.amount)
        };
        
        await sessionService.updateSessionProject(projectId, dataToSave, user.uid);
        setSaving(prev => ({ ...prev, [projectId]: false }));
        
        setProjects(prev => prev.map(project => 
          project.id === projectId 
            ? { ...project, ...dataToSave, updatedBy: user.uid, updatedAt: new Date() }
            : project
        ));
        
        if (!userCache[user.uid]) await getUserDisplayName(user.uid);
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [projectId]: false }));
      }
    }, 1500),
    [user.uid, projects, isAdmin]
  );

  // ADD NEW PROJECT
  const handleAddNew = async (departmentCode = null) => {
    try {
      const newProject = {
        ...DEFAULT_SESSION_PROJECT,
        financialYear: selectedFinancialYear,
        department: departmentCode || filters.department || '',
        projectName: `New Project ${projects.length + 1}`,
        amount: 0,
        clearanceType: 'state_plan',
        source: '',
        projectStatus: 'new_project',
        isProjectLocked: false
      };
      
      const result = await sessionService.addSessionProject(newProject, user.uid);
      if (result.success) {
        const cleanedProject = {
          ...result.data,
          amount: safeParseFloat(result.data.amount)
        };
        
        setProjects(prev => [cleanedProject, ...prev]);
        
        setSuccess(true);
        setSuccessMessage(`New project added successfully for FY ${selectedFinancialYear}!`);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error in handleAddNew:', error);
      setError('Failed to add new project: ' + error.message);
    }
  };

  // DELETE PROJECT
  const handleDelete = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    
    if (!project) return;

    if (project.isProjectLocked && !isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCKED_EDIT_WARNING);
      return;
    }

    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await sessionService.deleteSessionProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      setSuccess(true);
      setSuccessMessage('Project deleted successfully!');
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      setError('Failed to delete project: ' + error.message);
    }
  };

  // FIELD CHANGE HANDLER
  const handleFieldChange = (projectId, field, value) => {
    if (!projects.length) return;

    const project = projects.find(p => p.id === projectId);
    
    if (!project) return;

    if (project.isProjectLocked && !isAdmin) {
      alert(LOCK_MESSAGES.PROJECT_LOCKED_EDIT_WARNING);
      return;
    }

    let processedValue = value;
    
    if (field === 'amount') {
      processedValue = safeParseFloat(value);
    }

    setErrors(prev => ({
      ...prev,
      [`${projectId}.${field}`]: ''
    }));

    const updatedProject = { ...project, [field]: processedValue };
    const validation = validateSessionProject(updatedProject);
    
    if (!validation.isValid && validation.errors[field]) {
      setErrors(prev => ({
        ...prev,
        [`${projectId}.${field}`]: validation.errors[field]
      }));
      return;
    }

    if (value !== undefined && value !== null && value !== '') {
      debouncedAutoSave(projectId, updatedProject);
    }
  };

  // EXPORT HANDLER
  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      
      const exportData = {
        projects: filteredProjects,
        totals: totals,
        departmentTotals: departmentTotals
      };
      
      exportSessionBudgetDescriptiveToExcel(exportData, filters, getDepartmentLabel);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setError('Failed to export Excel. Please try again.');
    } finally {
      setExportingExcel(false);
    }
  };

  // FINANCIAL YEAR HANDLERS
  const handleFinancialYearChange = (year) => {
    setSelectedFinancialYear(year);
    setViewAllYears(false);
  };

  const handleViewAllYears = () => {
    setViewAllYears(!viewAllYears);
  };

  // GROUPING FUNCTION
  const getGroupedProjects = () => {
    if (!filteredProjects.length || departments.length === 0) return {};
    
    const grouped = {};
    filteredProjects.forEach(project => {
      if (!grouped[project.department]) {
        grouped[project.department] = [];
      }
      grouped[project.department].push(project);
    });
    
    const sortedGrouped = {};
    const departmentKeys = Object.keys(grouped);
    
    const unknownDepts = departmentKeys.filter(dept => 
      !dept || dept === '' || dept === 'undefined' || dept === 'null' || 
      dept.toLowerCase() === 'unknown' || dept === 'Select Dept.' || dept === 'select dept.'
    );
    
    const knownDepts = departmentKeys.filter(dept => 
      dept && dept !== '' && dept !== 'undefined' && dept !== 'null' && 
      dept.toLowerCase() !== 'unknown' && dept !== 'Select Dept.' && dept !== 'select dept.'
    );
    
    unknownDepts.forEach(dept => {
      const displayKey = dept || 'unknown';
      sortedGrouped[displayKey] = grouped[dept].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    });
    
    const sortedKnownDepts = knownDepts.sort((a, b) => {
      const deptA = departments.find(d => d.departmentCode === a);
      const deptB = departments.find(d => d.departmentCode === b);
      
      const priorityA = deptA?.priority || 999;
      const priorityB = deptB?.priority || 999;
      
      return priorityA - priorityB;
    });
    
    sortedKnownDepts.forEach(dept => {
      sortedGrouped[dept] = grouped[dept].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    });
    
    return sortedGrouped;
  };

  const fyOptions = financialYearUtils.getExtendedFinancialYearOptions();

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading session budget details..." />
      </div>
    );
  }

  return (
    <div>
      {/* SUCCESS MESSAGE */}
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="ml-2 text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .table-container {
          max-height: calc(100vh - 300px);
          overflow-y: auto;
          position: relative;
        }
        
        .sticky-header thead {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .sticky-header thead th {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }
        
        .no-arrows::-webkit-outer-spin-button,
        .no-arrows::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        .no-arrows[type=number] {
          -moz-appearance: textfield;
        }
        
        .enhanced-input:focus {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        .enhanced-input {
          transition: all 0.2s ease-in-out;
        }
        
        .enhanced-select {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
        
        .table-row:hover .enhanced-input {
          border-color: #3b82f6;
        }
      `}</style>

      {/* HEADER */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          {selectedFinancialYear && !viewAllYears && (
            <p className="text-3xl font-semibold text-blue-600 mt-1">
              {financialYearUtils.getFinancialYearDisplay(selectedFinancialYear)}
            </p>
          )}
          <h1 className={`${titleSize} font-semibold text-text-primary mb-2`}>Project Wise</h1>
          <p className="text-text-secondary">
            Detailed project-wise budget allocation for specific department
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <ExportExcelButton 
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={filteredProjects.length === 0}
          />

          <button
            onClick={() => setShowUpdatedByColumn(!showUpdatedByColumn)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              showUpdatedByColumn
                ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title={showUpdatedByColumn ? "Hide Updated By column" : "Show Updated By column"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm font-medium">
              {showUpdatedByColumn ? 'Hide' : 'Show'} Updated By
            </span>
          </button>

          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
          />
        </div>
      </div>

      {/* FINANCIAL YEAR SELECTOR */}
      <div className="bg-white border-2 border-blue-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500 rounded-lg p-2.5">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-900">Financial Year</label>
              <p className="text-xs text-gray-600">View and add projects for selected year</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={selectedFinancialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              disabled={viewAllYears}
              className={`px-4 py-2 text-sm font-medium border-2 rounded-lg transition-all duration-200 ${
                viewAllYears 
                  ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-white border-blue-300 text-gray-900 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer'
              }`}
            >
              {fyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}{option.isCurrent}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleViewAllYears}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                viewAllYears
                  ? 'bg-blue-500 text-white border-2 border-blue-500 shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span>{viewAllYears ? 'Viewing All Years' : 'View All Years'}</span>
              </div>
            </button>
          </div>
        </div>
        
        {!viewAllYears && (
          <div className="mt-3 pt-3 border-t border-blue-100">
            <p className="text-xs text-gray-600">
              <span className="font-medium text-blue-700">Note:</span> New projects will be added to <span className="font-semibold text-blue-900">FY {selectedFinancialYear}</span>
            </p>
          </div>
        )}
      </div>

      {/* ERROR MESSAGE */}
      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* MAIN TABLE */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={hasActiveFilters ? "No projects match your filters" : "No session projects found"}
              description={
                hasActiveFilters 
                  ? "Try adjusting your filter criteria to see more projects"
                  : filters.department 
                    ? `No session projects found for ${getDepartmentLabel(filters.department)} in ${selectedFinancialYear || 'selected period'}`
                    : `No session projects found for ${selectedFinancialYear || 'selected period'}`
              }
              actionLabel={hasActiveFilters ? "Clear Filters" : "Add First Project"}
              onAction={hasActiveFilters ? clearFilters : handleAddNew}
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="min-w-full text-xs sticky-header">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S. No.
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    Project name
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Source
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-42">
                    Project Status
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    Amount
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className={`px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-42 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                    State Plan/<br/>Negotiated Loan
                  </th>
                  {showUpdatedByColumn && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                      Updated By
                    </th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* ADD NEW ROW */}
                <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                  <td className="px-3 py-3 text-center border-r border-gray-200">
                    <button
                      onClick={() => handleAddNew()}
                      className="group flex items-center justify-center w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-lg hover:shadow-xl"
                      title="Add New Project"
                    >
                      <svg className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-xs text-blue-600 font-medium border-r border-gray-200">
                    Click + to add new project
                  </td>
                  <td className="px-3 py-3 border-r border-gray-200"></td>
                  <td className="px-3 py-3 border-r border-gray-200"></td>
                  <td className="px-3 py-3 border-r border-gray-200"></td>
                  <td className="px-3 py-3 border-r border-gray-200"></td>
                  <td className={`px-3 py-3 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}></td>
                  {showUpdatedByColumn && (
                    <td className="px-3 py-3 border-r border-gray-200"></td>
                  )}
                  <td className="px-3 py-3"></td>
                </tr>

                {/* PROJECT ROWS */}
                {(() => {
                  if (filters.department) {
                    // Single department view
                    const sortedProjects = [...filteredProjects].sort((a, b) => {
                      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                      return dateB - dateA;
                    });
                    
                    return sortedProjects.map((project, index) => {
                      const isProjectSaving = saving[project.id];
                      const lastUpdated = formatLastUpdated(project.updatedBy || project.createdBy, project.updatedAt || project.createdAt);
                      const isProjectLocked = project.isProjectLocked || false;
                      const canEdit = canEditProject(project);
                      
                      return (
                        <tr key={project.id} className={`table-row hover:bg-blue-50 transition-colors duration-200 ${isProjectLocked ? 'bg-orange-50' : ''}`}>
                          {/* Serial Number */}
                          <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                            <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                              {index + 1}
                            </div>
                          </td>
                          
                          {/* Department */}
                          <td className="px-3 py-3 border-r border-gray-200">
                            <select
                              value={project.department || ''}
                              onChange={(e) => handleFieldChange(project.id, 'department', e.target.value)}
                              disabled={!canEdit}
                              className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <option value="">Select Dept.</option>
                              {getDepartmentOptions().map(dept => (
                                <option key={dept.value} value={dept.value}>
                                  {dept.label}
                                </option>
                              ))}
                            </select>
                            {isProjectSaving && (
                              <div className="flex items-center mt-1">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                <span className="ml-1 text-xs text-blue-600">Saving...</span>
                              </div>
                            )}
                          </td>
                          
                          {/* Project Name - DEBOUNCED */}
                          <td className="px-3 py-3 border-r border-gray-200">
                            <DebouncedInput
                              type="text"
                              value={project.projectName || ''}
                              onChange={(value) => handleFieldChange(project.id, 'projectName', value)}
                              disabled={!canEdit}
                              delay={1200}
                              className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              placeholder="Enter project name"
                            />
                          </td>
                          
                          {/* Source - DEBOUNCED */}
                          <td className="px-3 py-3 border-r border-gray-200">
                            <DebouncedInput
                              type="text"
                              value={project.source || ''}
                              onChange={(value) => handleFieldChange(project.id, 'source', value)}
                              disabled={!canEdit}
                              delay={1200}
                              className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              placeholder="Enter source"
                            />
                          </td>
                          
                          {/* Project Status */}
                          <td className="px-3 py-3 text-center border-r border-gray-200">
                            <select
                              value={project.projectStatus || 'new_project'}
                              onChange={(e) => handleFieldChange(project.id, 'projectStatus', e.target.value)}
                              disabled={!canEdit}
                              className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {SESSION_PROJECT_STATUS_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          
                          {/* Amount - DEBOUNCED */}
                          <td className="px-3 py-3 text-center border-r border-gray-200">
                            <DebouncedInput
                              type="number"
                              value={project.amount || ''}
                              onChange={(value) => handleFieldChange(project.id, 'amount', value)}
                              disabled={!canEdit}
                              delay={1200}
                              className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          
                          {/* Clearance Type */}
                          <td className={`px-3 py-3 text-center ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                            <select
                              value={project.clearanceType || ''}
                              onChange={(e) => handleFieldChange(project.id, 'clearanceType', e.target.value)}
                              disabled={!canEdit}
                              className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <option value="">Select Type</option>
                              {SESSION_CLEARANCE_TYPE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          
                          {/* Updated By */}
                          {showUpdatedByColumn && (
                            <td className="px-3 py-3 text-center border-r border-gray-200">
                              <div className="text-xs text-gray-600">
                                <div className="font-medium text-gray-800 mb-0.5">
                                  {lastUpdated.user}
                                </div>
                                <div className="text-gray-500">
                                  {lastUpdated.time}
                                </div>
                              </div>
                            </td>
                          )}
                          
                          {/* Actions */}
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center space-x-1">
                              {isProjectLocked ? (
                                isAdmin ? (
                                  <button
                                    onClick={() => handleUnlockProject(project.id)}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title={`Unlock project (Locked by ${project.projectLockedByName})`}
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                    </svg>
                                  </button>
                                ) : (
                                  <div className="p-1.5 text-orange-400" title={`Locked by ${project.projectLockedByName}`}>
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )
                              ) : (
                                <button
                                  onClick={() => handleLockProject(project.id)}
                                  disabled={isProjectSaving}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Lock project"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </button>
                              )}
                              
                              {canEdit && (
                                <button
                                  onClick={() => handleDelete(project.id)}
                                  disabled={isProjectSaving}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete project"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  } else {
                    // Grouped by department view
                    const groupedProjects = getGroupedProjects();
                    const sortedDepartmentKeys = Object.keys(groupedProjects);
                    const rows = [];
                    
                    sortedDepartmentKeys.forEach((department) => {
                      const deptProjects = groupedProjects[department];
                      
                      // Department Header Row
                      rows.push(
                        <tr key={`header-${department}`} className="bg-gray-100">
                          <td className="px-3 py-3 text-xs font-bold text-gray-900 border-r border-gray-200">
                            <button
                              onClick={() => handleAddNew(department)}
                              className="group flex items-center justify-center w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full transition-all duration-200 transform hover:scale-110 shadow-md hover:shadow-lg"
                              title={`Add project to ${getDepartmentLabel(department)}`}
                            >
                              <svg className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </td>
                          <td className="px-3 py-3 text-xs font-bold text-gray-900 uppercase border-r border-gray-200">
                            {getDepartmentLabel(department)}
                          </td>
                          <td className="px-3 py-3 border-r border-gray-200"></td>
                          <td className="px-3 py-3 border-r border-gray-200"></td>
                          <td className="px-3 py-3 border-r border-gray-200"></td>
                          <td className="px-3 py-3 border-r border-gray-200"></td>
                          <td className={`px-3 py-3 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}></td>
                          {showUpdatedByColumn && (
                            <td className="px-3 py-3 border-r border-gray-200"></td>
                          )}
                          <td className="px-3 py-3"></td>
                        </tr>
                      );
                      
                      // Project Rows
                      deptProjects.forEach((project) => {
                        const isProjectSaving = saving[project.id];
                        const lastUpdated = formatLastUpdated(project.updatedBy || project.createdBy, project.updatedAt || project.createdAt);
                        const isProjectLocked = project.isProjectLocked || false;
                        const canEdit = canEditProject(project);
                        
                        rows.push(
                          <tr key={project.id} className={`table-row hover:bg-blue-50 transition-colors duration-200 ${isProjectLocked ? 'bg-orange-50' : ''}`}>
                            <td className="px-3 py-3 border-r border-gray-200"></td>
                            <td className="px-3 py-3 border-r border-gray-200">
                              <select
                                value={project.department || ''}
                                onChange={(e) => handleFieldChange(project.id, 'department', e.target.value)}
                                disabled={!canEdit}
                                className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <option value="">Select Dept.</option>
                                {getDepartmentOptions().map(dept => (
                                  <option key={dept.value} value={dept.value}>
                                    {dept.label}
                                  </option>
                                ))}
                              </select>
                              {isProjectSaving && (
                                <div className="flex items-center mt-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                  <span className="ml-1 text-xs text-blue-600">Saving...</span>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-900 font-medium pl-8 border-r border-gray-200">
                              <DebouncedInput
                                type="text"
                                value={project.projectName || ''}
                                onChange={(value) => handleFieldChange(project.id, 'projectName', value)}
                                disabled={!canEdit}
                                delay={1200}
                                className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder="Enter project name"
                              />
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-900 font-medium border-r border-gray-200">
                              <DebouncedInput
                                type="text"
                                value={project.source || ''}
                                onChange={(value) => handleFieldChange(project.id, 'source', value)}
                                disabled={!canEdit}
                                delay={1200}
                                className={`enhanced-input w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder="Enter source"
                              />
                            </td>
                            <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">
                              <select
                                value={project.projectStatus || 'new_project'}
                                onChange={(e) => handleFieldChange(project.id, 'projectStatus', e.target.value)}
                                disabled={!canEdit}
                                className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {SESSION_PROJECT_STATUS_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">
                              <DebouncedInput
                                type="number"
                                value={project.amount || ''}
                                onChange={(value) => handleFieldChange(project.id, 'amount', value)}
                                disabled={!canEdit}
                                delay={1200}
                                className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder="0"
                              />
                            </td>
                            <td className={`px-3 py-3 text-center text-xs text-gray-900 font-medium ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                              <select
                                value={project.clearanceType || ''}
                                onChange={(e) => handleFieldChange(project.id, 'clearanceType', e.target.value)}
                                disabled={!canEdit}
                                className={`enhanced-input enhanced-select w-full text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <option value="">Select Type</option>
                                {SESSION_CLEARANCE_TYPE_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            {showUpdatedByColumn && (
                              <td className="px-3 py-3 text-center border-r border-gray-200">
                                <div className="text-xs text-gray-600">
                                  <div className="font-medium text-gray-800 mb-0.5">
                                    {lastUpdated.user}
                                  </div>
                                  <div className="text-gray-500">
                                    {lastUpdated.time}
                                  </div>
                                </div>
                              </td>
                            )}
                            <td className="px-3 py-3 text-center">
                              <div className="flex items-center justify-center space-x-1">
                                {isProjectLocked ? (
                                  isAdmin ? (
                                    <button
                                      onClick={() => handleUnlockProject(project.id)}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title={`Unlock project (Locked by ${project.projectLockedByName})`}
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  ) : (
                                    <div className="p-1.5 text-orange-400" title={`Locked by ${project.projectLockedByName}`}>
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )
                                ) : (
                                  <button
                                    onClick={() => handleLockProject(project.id)}
                                    disabled={isProjectSaving}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Lock project"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                  </button>
                                )}
                                
                                {canEdit && (
                                  <button
                                    onClick={() => handleDelete(project.id)}
                                    disabled={isProjectSaving}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Delete project"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      });
                      
                      // Department Total Row
                      const deptTotal = departmentTotals[department] || 0;
                      
                      rows.push(
                        <tr key={`total-${department}`} className="bg-gray-700 text-white font-bold border-t-2 border-gray-600">
                          <td className="px-3 py-3 border-r border-gray-600"></td>
                          <td className="px-3 py-3 text-xs font-bold border-r border-gray-600">
                            Total - {getDepartmentLabel(department)}
                          </td>
                          <td className="px-3 py-3 border-r border-gray-600"></td>
                          <td className="px-3 py-3 border-r border-gray-600"></td>
                          <td className="px-3 py-3 border-r border-gray-600"></td>
                          <td className="px-3 py-3 text-center text-xs font-bold border-r border-gray-600">
                            {formatSessionAmount(deptTotal)}
                          </td>
                          <td className={`px-3 py-3 ${showUpdatedByColumn ? 'border-r border-gray-600' : ''}`}></td>
                          {showUpdatedByColumn && (
                            <td className="px-3 py-3 border-r border-gray-600"></td>
                          )}
                          <td className="px-3 py-3"></td>
                        </tr>
                      );
                    });
                    
                    return rows;
                  }
                })()}

                {/* GRAND TOTAL ROW */}
                {filteredProjects.length > 0 && (
                  <tr className="bg-gray-800 text-white font-bold border-t-4 border-gray-700">
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-xs font-bold border-r border-gray-700">
                      {filters.department ? 'Total' : 'Grand Total'}
                      {hasActiveFilters && ' (Filtered)'}
                    </td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 border-r border-gray-700"></td>
                    <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-700">
                      {formatSessionAmount(totals.totalFinancialRequirement)}
                    </td>
                    <td className={`px-3 py-3 ${showUpdatedByColumn ? 'border-r border-gray-700' : ''}`}></td>
                    {showUpdatedByColumn && (
                      <td className="px-3 py-3 border-r border-gray-700"></td>
                    )}
                    <td className="px-3 py-3"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionBudgetDescriptive;